import _get from 'lodash/get';
import _orderBy from 'lodash/orderBy';
import type { FilterFn, Row } from '@tanstack/react-table';

import type { Option as OptionDTO } from 'shared/models/common';
import type { DeepKeys } from 'shared/models/deep-keys';
import type { Option } from 'shared/components/TableFilterSelect';

type StatusEntity = {
  Status: OptionDTO<number> | null;
};

type CreatedOnEntity = {
  CreatedOn: string | null;
};

export type RangeFilterValue = {
  startDate: Date | null;
  endDate: Date | null;
};

function toOption(dto: OptionDTO): Option<number> {
  return {
    label: dto.Label ?? '',
    value: dto.Value ?? 0,
  };
}

function toYearOption(year: number): Option<number> {
  return {
    label: String(year),
    value: year,
  };
}

function isUniqueOption<T extends string | number>(
  checkedOption: Option<T>,
  list: Option<T>[]
) {
  return !list.some((option) => option.value === checkedOption.value);
}

export const createStatusOptionFilterFn = <
  T extends StatusEntity
>(): FilterFn<T> => {
  return (row, _columnId, value) => {
    if (Array.isArray(value) && value.length) {
      return value.includes(row.original.Status?.Value);
    }

    return false;
  };
};

export const createYearFilterFn = <T extends CreatedOnEntity>(
  key: DeepKeys<T>
): FilterFn<T> => {
  return (row, _columnId, value) => {
    if (Array.isArray(value) && value.length) {
      const originalValue = _get(row.original, key);
      const year =
        typeof originalValue === 'string'
          ? new Date(originalValue).getFullYear()
          : null;
      return value.includes(year);
    }

    return false;
  };
};

export function getYearOptions<T extends object>(
  rows: Row<T>[],
  key: DeepKeys<T>
): Option<number>[] {
  const options = rows.reduce((acc, row) => {
    const originalValue = _get(row.original, key);
    if (typeof originalValue === 'string') {
      const option = toYearOption(new Date(originalValue).getFullYear());

      if (isUniqueOption(option, acc)) acc.push(option);
    }

    return acc;
  }, [] as Option<number>[]);

  return _orderBy(options, 'value', 'desc');
}

export function getStatusOptions<T extends StatusEntity>(
  rows: Row<T>[]
): Option<number>[] {
  const options = rows.reduce((acc, row) => {
    if (row.original.Status) {
      const option = toOption(row.original.Status);

      if (isUniqueOption(option, acc)) acc.push(option);
    }

    return acc;
  }, [] as Option<number>[]);

  return _orderBy(options, 'label', 'asc');
}
